import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import useScrollReveal from '../useScrollReveal';
import useLeadSubmit, { Honeypot } from '../useLeadSubmit';
import { sectionStyles, getCardClass, isCardTransparent, getAnimation, hexToRgba, buildSrcSet, getCardRadius, getCardGap, getGridCols } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import SectionBgImage from './SectionBgImage';

export default function ContactSection({ block = {}, brand, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {}, careers = [], currentPage }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const [waName, setWaName] = useState('');
    const [waEmail, setWaEmail] = useState('');
    const [waCareer, setWaCareer] = useState('');
    const leadSubmit = useLeadSubmit({
        brandId: brand?.id,
        blockType: 'contact',
        blockId: block.id,
        pageId: currentPage?.id || null,
        source: 'Contacto WhatsApp',
    });

    const b = brand || {};
    const hasContact = b.contact_email || b.contact_phone || b.whatsapp || b.address || b.business_hours;

    // Use placeholder data if no contact info exists (for preview)
    const displayBrand = hasContact ? b : {
        contact_email: 'info@ejemplo.com',
        contact_phone: '+51 999 999 999',
        whatsapp: '',
        address: 'Av. Principal 123, Lima',
        business_hours: 'Lun-Vie 9:00 - 18:00',
    };

    const showWhatsApp = config.show_whatsapp_button && displayBrand.whatsapp;
    const waPhone = displayBrand.whatsapp ? displayBrand.whatsapp.replace(/\D/g, '') : '';
    const buildWhatsAppUrl = () => {
        const parts = [];
        if (waName) parts.push(`mi nombre es ${waName}`);
        if (waCareer) parts.push(`Me interesa ${waCareer}`);
        if (waEmail) parts.push(`Mi email es ${waEmail}`);
        const msg = parts.length > 0 ? `Hola, ${parts.join('. ')}.` : (content.whatsapp_message || 'Hola, me gustaria recibir mas informacion.');
        return `https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}`;
    };

    const title = content.title || 'Contacto';
    const card = getCardClass(cardStyle, s.dark);
    const tp = isCardTransparent(cardStyle, s.dark);
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'cards';
    const sectionCls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;

    const ItemWrapper = ({ item, className, style, children }) => {
        if (item.href) {
            return <a href={item.href} target={item.target} rel={item.target ? 'noopener noreferrer' : undefined} className={className} style={style}>{children}</a>;
        }
        return <div className={className} style={style}>{children}</div>;
    };

    const iconColor = config.card_icon_color || s.accent;
    const items = [
        config.show_email !== false && displayBrand.contact_email && { href: `mailto:${displayBrand.contact_email}`, icon: 'email', title: content.email_title || 'Email', desc: content.email_desc || displayBrand.contact_email },
        config.show_phone !== false && displayBrand.contact_phone && { href: `tel:${displayBrand.contact_phone}`, icon: 'phone', title: content.phone_title || 'Telefono', desc: content.phone_desc || displayBrand.contact_phone },
        config.show_whatsapp !== false && displayBrand.whatsapp && { href: `https://wa.me/${displayBrand.whatsapp.replace(/\D/g, '')}`, target: '_blank', icon: 'whatsapp', title: content.whatsapp_title || 'WhatsApp', desc: content.whatsapp_desc || 'Escribenos al WhatsApp' },
        config.show_address !== false && displayBrand.address && { icon: 'address', title: content.address_title || 'Direccion', desc: content.address_desc || displayBrand.address },
        config.show_hours !== false && displayBrand.business_hours && { icon: 'hours', title: content.hours_title || 'Horario', desc: content.hours_desc || displayBrand.business_hours },
    ].filter(Boolean);

    const icons = {
        email: <svg className="w-6 h-6" style={{color: iconColor}} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>,
        phone: <svg className="w-6 h-6" style={{color: iconColor}} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>,
        whatsapp: <svg className="w-6 h-6" style={{color: config.card_icon_color || '#16a34a'}} fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>,
        address: <svg className="w-6 h-6" style={{color: iconColor}} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>,
        hours: <svg className="w-6 h-6" style={{color: iconColor}} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>,
    };

    const whatsappIcon = <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>;

    const inputBaseStyle = s.dark
        ? { backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: '#ffffff' }
        : { backgroundColor: '#ffffff', borderColor: '#d1d5db', color: s.text };
    const inputCls = `w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:outline-none transition-all ${
        s.dark ? 'placeholder-white/50' : 'placeholder-gray-400'
    }`;

    const WhatsAppForm = () => (
        <div className="mt-8 max-w-md mx-auto relative">
            <Honeypot onFill={leadSubmit.setHoneypot} />
            <div className="space-y-3">
                <input type="text" placeholder="Tu nombre" value={waName} onChange={e => setWaName(e.target.value)} className={inputCls} style={inputBaseStyle} />
                <input type="email" placeholder="Tu email" value={waEmail} onChange={e => setWaEmail(e.target.value)} className={inputCls} style={inputBaseStyle} />
                {careers.length > 0 && (
                    <select value={waCareer} onChange={e => setWaCareer(e.target.value)} className={inputCls} style={inputBaseStyle}>
                        <option value="">Carrera de interes</option>
                        {careers.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                )}
                <a
                    href={buildWhatsAppUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => { if (waName || waEmail) leadSubmit.submit({ name: waName, email: waEmail, carrera: waCareer }); }}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-white font-semibold text-sm transition-all hover:opacity-90 hover:shadow-lg"
                    style={{ backgroundColor: '#25D366' }}
                >
                    {whatsappIcon}
                    {content.whatsapp_btn || 'Escribenos por WhatsApp'}
                </a>
            </div>
        </div>
    );

    // ── Inline variant ──
    if (variant === 'inline') {
        return (
            <section className={sectionCls} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 ${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-10">
                        {content.title && <h2 className="text-xl font-bold shrink-0" style={{ color: s.text }}>{title}</h2>}
                        {items.map((item, i) => (
                            <ItemWrapper key={i} item={item}
                                className="inline-flex items-center gap-2 text-sm font-medium hover:underline transition-colors" style={{ color: s.accent }}>
                                {icons[item.icon]}
                                <span>{item.desc}</span>
                            </ItemWrapper>
                        ))}
                    </div>
                    {showWhatsApp && <WhatsAppForm />}
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Big icons ──
    if (variant === 'big-icons') {
        return (
            <section className={sectionCls} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 ${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 text-center ${anim}`}>
                    <SectionHeader tag={content.tag || 'Comunicate'} title={title} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`grid gap-10 ${items.length <= 2 ? (items.length === 1 ? 'max-w-xs mx-auto' : 'grid-cols-2 max-w-2xl mx-auto') : items.length === 3 ? 'grid-cols-3 max-w-4xl mx-auto' : 'grid-cols-2 md:grid-cols-4 max-w-5xl mx-auto'}`}>
                        {items.map((item, i) => (
                            <ItemWrapper key={i} item={item}
                                className={`group text-center transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${i * 150}ms` : '0ms' }}>
                                <div className="w-20 h-20 rounded-3xl mx-auto mb-4 flex items-center justify-center transition-transform group-hover:scale-110 group-hover:shadow-lg"
                                    style={{ backgroundColor: hexToRgba(iconColor, 0.07) }}>
                                    <div className="scale-150">{icons[item.icon]}</div>
                                </div>
                                <h3 className="font-bold text-lg mb-1" style={{ color: config.card_text || s.text }}>{item.title}</h3>
                                <p className="text-sm" style={{ color: config.card_text ? hexToRgba(config.card_text, 0.7) : s.textSub }}>{item.desc}</p>
                            </ItemWrapper>
                        ))}
                    </div>
                    {showWhatsApp && <WhatsAppForm />}
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Banner ──
    if (variant === 'banner') {
        return (
            <section className="relative overflow-hidden" style={{ backgroundColor: s.accent }}>
                <div ref={ref} className={`relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-center gap-6 ${anim}`}>
                    {items.map((item, i) => (
                        <ItemWrapper key={i} item={item}
                            className="inline-flex items-center gap-2 text-white/90 hover:text-white text-sm font-medium transition-colors">
                            <div className="w-5 h-5 text-white">{icons[item.icon]}</div>
                            <span>{item.desc}</span>
                        </ItemWrapper>
                    ))}
                </div>
            </section>
        );
    }

    // ── Split ──
    if (variant === 'split') {
        const splitRight = config.split_right || 'image';
        // Resolve split image from ImagePicker
        const splitImageFile = (() => {
            if (content.split_image_folder_id && content.split_image_file_id && mediaFolders[content.split_image_folder_id]) {
                return mediaFolders[content.split_image_folder_id].find(x => String(x.id) === String(content.split_image_file_id)) || null;
            }
            return null;
        })();
        const splitImage = splitImageFile?.image_url || content.split_image_url || null;

        const SplitContactForm = () => {
            const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
            return (
                <div className={`rounded-2xl p-6 lg:p-8 ${getCardClass(cardStyle, s.dark)}`}>
                    <h3 className="text-lg font-bold mb-4" style={{ color: s.text }}>Enviar mensaje</h3>
                    <Honeypot onFill={leadSubmit.setHoneypot} />
                    <div className="space-y-3">
                        <input type="text" placeholder="Tu nombre" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className={inputCls} style={inputBaseStyle} />
                        <input type="email" placeholder="Tu email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className={inputCls} style={inputBaseStyle} />
                        <input type="tel" placeholder="Tu telefono" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className={inputCls} style={inputBaseStyle} />
                        <textarea placeholder="Tu mensaje" rows={3} value={form.message} onChange={e => setForm({...form, message: e.target.value})} className={inputCls} style={inputBaseStyle} />
                        <button
                            onClick={() => leadSubmit.submit({ name: form.name, email: form.email, phone: form.phone, message: form.message })}
                            disabled={leadSubmit.loading || leadSubmit.sent}
                            className="w-full px-6 py-3 rounded-lg text-white font-semibold text-sm transition-all hover:opacity-90 hover:shadow-lg disabled:opacity-50"
                            style={{ backgroundColor: s.accent }}>
                            {leadSubmit.sent ? 'Enviado' : leadSubmit.loading ? 'Enviando...' : 'Enviar mensaje'}
                        </button>
                        {leadSubmit.sent && <p className="text-green-600 text-xs text-center">Mensaje enviado correctamente</p>}
                    </div>
                </div>
            );
        };

        const rightContent = (() => {
            if (splitRight === 'whatsapp' && displayBrand.whatsapp) return <WhatsAppForm />;
            if (splitRight === 'form') return <SplitContactForm />;
            if (splitRight === 'map') {
                const mapQuery = config.map_mode === 'coords' && content.map_lat && content.map_lng
                    ? `${content.map_lat},${content.map_lng}`
                    : displayBrand.address;
                if (mapQuery) return (
                    <div className="rounded-2xl overflow-hidden h-64 lg:h-full min-h-[320px] shadow-lg">
                        <iframe
                            src={`https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&output=embed`}
                            className="w-full h-full border-0" loading="lazy" allowFullScreen
                            title="Ubicacion"/>
                    </div>
                );
            }
            if (splitRight === 'image' && splitImage) return (
                <div className="rounded-2xl overflow-hidden h-64 lg:h-full min-h-[320px] shadow-lg">
                    <img src={splitImage} alt={title} className="w-full h-full object-cover" loading="lazy" decoding="async" srcSet={buildSrcSet(splitImageFile)} sizes="(max-width:1024px) 100vw, 50vw"/>
                </div>
            );
            // Fallback placeholder
            return (
                <div className="rounded-2xl h-64 lg:h-80 flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${hexToRgba(s.accent, 0.06)}, ${hexToRgba(s.accent, 0.15)})` }}>
                    <svg className="w-20 h-20 opacity-20" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                </div>
            );
        })();

        return (
            <section className={sectionCls} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 ${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <SectionHeader tag={content.tag || 'Comunicate'} title={title} subtitle={content.subtitle} config={{...config, title_decoration: 'none'}} s={s} />
                            <div className="space-y-4 mt-6">
                                {items.map((item, i) => (
                                    <ItemWrapper key={i} item={item}
                                        className="flex items-center gap-4 group">
                                        <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-110" style={{ backgroundColor: hexToRgba(s.accent, 0.07) }}>
                                            {icons[item.icon]}
                                        </div>
                                        <div>
                                            <p className="font-semibold text-sm" style={{ color: s.text }}>{item.title}</p>
                                            <p className="text-sm" style={{ color: s.textSub }}>{item.desc}</p>
                                        </div>
                                    </ItemWrapper>
                                ))}
                            </div>
                        </div>
                        {rightContent}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Full Form ──
    if (variant === 'full-form') {
        const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
        const formLeadSubmit = useLeadSubmit({
            brandId: brand?.id,
            blockType: 'contact',
            blockId: block.id,
            pageId: currentPage?.id || null,
            source: 'Contacto Formulario',
        });
        return (
            <section className={sectionCls} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center ${anim}`}>
                    <SectionHeader tag={content.tag || 'Contacto'} title={title} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`${card} p-8 sm:p-10 shadow-lg rounded-2xl`}>
                        {formLeadSubmit.sent ? (
                            <div className="flex flex-col items-center gap-3 py-8">
                                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: hexToRgba(s.accent, 0.1) }}>
                                    <svg className="w-8 h-8" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
                                </div>
                                <h3 className="text-xl font-bold" style={{ color: s.text }}>¡Mensaje enviado!</h3>
                                <p className="text-sm" style={{ color: s.textSub }}>Nos pondremos en contacto contigo pronto.</p>
                            </div>
                        ) : (
                            <>
                                <Honeypot onFill={formLeadSubmit.setHoneypot} />
                                <div className="space-y-4">
                                    <input type="text" placeholder={content.name_placeholder || 'Tu nombre'} value={form.name} onChange={e => setForm({...form, name: e.target.value})} className={inputCls} style={inputBaseStyle} />
                                    <input type="email" placeholder={content.email_placeholder || 'Tu email'} value={form.email} onChange={e => setForm({...form, email: e.target.value})} className={inputCls} style={inputBaseStyle} />
                                    <input type="tel" placeholder={content.phone_placeholder || 'Tu telefono (opcional)'} value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className={inputCls} style={inputBaseStyle} />
                                    <textarea placeholder={content.message_placeholder || 'Tu mensaje'} rows={4} value={form.message} onChange={e => setForm({...form, message: e.target.value})} className={inputCls} style={inputBaseStyle} />
                                    <button
                                        onClick={() => formLeadSubmit.submit({ name: form.name, email: form.email, phone: form.phone, message: form.message })}
                                        disabled={formLeadSubmit.loading}
                                        className="w-full px-6 py-3 rounded-lg text-white font-semibold text-sm transition-all hover:opacity-90 hover:shadow-lg disabled:opacity-50"
                                        style={{ backgroundColor: s.accent }}>
                                        {formLeadSubmit.loading ? 'Enviando...' : (content.submit_text || 'Enviar mensaje')}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Office Locations ──
    if (variant === 'office-locations') {
        const locations = content.items || [
            { title: 'Oficina Central', description: 'Av. Principal 123, Lima', phone: '+51 999 999 999', email: 'info@ejemplo.com' },
            { title: 'Sucursal Norte', description: 'Calle Secundaria 456, Trujillo', phone: '+51 888 888 888', email: 'norte@ejemplo.com' },
        ];
        return (
            <section className={sectionCls} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 ${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag || 'Ubicaciones'} title={title} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`grid grid-cols-1 sm:grid-cols-2 ${locations.length >= 3 ? 'lg:grid-cols-3' : ''} gap-6`}>
                        {locations.map((loc, i) => (
                            <div key={i}
                                className={`group ${card} p-6 rounded-2xl transition-all duration-500 hover:-translate-y-1 hover:shadow-xl ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${i * 120}ms` : '0ms' }}>
                                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                                    style={{ backgroundColor: hexToRgba(s.accent, 0.08) }}>
                                    {icons.address}
                                </div>
                                <h3 className="font-bold text-lg mb-2" style={{ color: s.text }}>{loc.title}</h3>
                                {loc.description && <p className="text-sm mb-3" style={{ color: s.textSub }}>{loc.description}</p>}
                                <div className="space-y-1.5">
                                    {loc.phone && (
                                        <a href={`tel:${loc.phone}`} className="flex items-center gap-2 text-sm hover:underline transition-colors" style={{ color: s.accent }}>
                                            <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                                            {loc.phone}
                                        </a>
                                    )}
                                    {loc.email && (
                                        <a href={`mailto:${loc.email}`} className="flex items-center gap-2 text-sm hover:underline transition-colors" style={{ color: s.accent }}>
                                            <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                                            {loc.email}
                                        </a>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    {content.map_url && (
                        <div className="mt-10 rounded-2xl overflow-hidden shadow-lg" style={{ height: '350px' }}>
                            <iframe src={content.map_url} className="w-full h-full border-0" loading="lazy" allowFullScreen title="Mapa"/>
                        </div>
                    )}
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Cards (default) ──
    return (
        <section className={sectionCls} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
            <div ref={ref} className={`relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center ${anim}`}>
                <SectionHeader tag={content.tag || 'Comunicate'} title={title} subtitle={content.subtitle} config={config} s={s} />
                <div className={`grid ${getCardGap(config) || 'gap-6'} ${getGridCols(config) || (items.length <= 2 ? (items.length === 1 ? 'max-w-sm mx-auto' : 'grid-cols-1 sm:grid-cols-2 max-w-2xl mx-auto') : items.length === 3 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3')}`}>
                    {items.map((item, i) => (
                        <ItemWrapper key={i} item={item}
                            className={`group relative ${config.card_bg ? (getCardRadius(config) || 'rounded-2xl') : card} p-8 transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                            style={{ transitionDelay: visible ? `${200 + i * 100}ms` : '0ms', ...(config.card_bg ? { backgroundColor: config.card_bg } : {}) }}>
                            <div className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                                style={{ backgroundColor: tp ? 'rgba(255,255,255,0.1)' : hexToRgba(iconColor, 0.07) }}>
                                {icons[item.icon]}
                            </div>
                            <h3 className="font-bold text-lg mb-2" style={{ color: config.card_text || s.text }}>{item.title}</h3>
                            <p className="text-sm break-all transition-colors" style={{ color: config.card_text ? hexToRgba(config.card_text, 0.7) : s.textSub }}>{item.desc}</p>
                        </ItemWrapper>
                    ))}
                </div>
                {content.qr_url && (
                    <div className="flex flex-col items-center gap-2 mt-8">
                        <QRCodeSVG value={content.qr_url} size={100} level="M" fgColor={s.accent} bgColor="transparent" />
                        <span className="text-xs" style={{color: s.textSub}}>Escanea para contactar</span>
                    </div>
                )}
                {showWhatsApp && <WhatsAppForm />}
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
